import React, { useEffect, useRef, useState } from 'react';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import CustomNavigationButtons from 'components/CustomNavigationButtons/CustomNavigationButtons';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import OrganizationDetailsPage from './OrganizationDetails';
import DepartmentsPage from './Departments';
import Business from '@material-ui/icons/BusinessOutlined';
import LocationCity from '@material-ui/icons/LocationCity';
import { Redirect, Route, Switch } from 'react-router-dom';
import { AccountsService } from 'services/accounts/accounts';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { useDispatch } from 'react-redux';
import styles from 'assets/jss/material-dashboard-pro-react/views/accounts/accountDetailsStyle';
import { makeStyles } from '@material-ui/core';
import { translate } from 'components/Translation';
import { finalize } from 'rxjs/operators';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';

const useStyles = makeStyles(styles);

interface IOrganizationDetailsProps {
	match: {
		params: {
			subpage?: string;
			accountId: string;
		};
		path: string;
		url: string;
	};
	location: {
		pathname: string;
	};
}

const Details = (props: IOrganizationDetailsProps) => {
	const classes = useStyles();
	let { subpage = 'organization-details', accountId } = props.match.params;
	let { pathname } = props.location;
	const [loading, setLoading] = useState<boolean>(true);
	const [accounts, setAccounts] = useState<SingleCorpAccount[]>([]);
	const [account, setAccount] = useState<SingleCorpAccount | undefined>();
	const isEnterprise = accounts.length > 1;
	const path = `/dashboard/organizations/${accountId}`;
	const dispatch = useDispatch();
	const isMounted = useRef(true);

	const showNavigationButtons = ['organization-details', 'departments'].some((element) => pathname.endsWith(element));

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		dispatch(showLoadingIndicator());
		AccountsService.getCorpAccountsWithAdminsForCurrentUser()
			.pipe(
				finalize(() => {
					dispatch(hideLoadingIndicator());
					if (isMounted.current) {
						setLoading(false);
					}
				})
			)
			.subscribe(({ accounts }) => {
				if (isMounted.current) {
					setAccounts(accounts);
					setAccount(accounts.find((account) => account.accountId === accountId));
				}
			});
		return () => {
			dispatch(hideLoadingIndicator());
		};
	}, [dispatch, accountId]);

	return (
		<GridContainer>
			<ConditionalRender
				show={isEnterprise}
				onTrue={() => (
					<BreadcrumbsItem to={`/dashboard/organizations`}>
						{translate('path.my.organizations')}
					</BreadcrumbsItem>
				)}
			/>
			<ConditionalRender
				show={showNavigationButtons}
				onTrue={() => (
					<GridItem xs={12}>
						<CustomNavigationButtons
							className={classes.filters}
							path={path}
							tabs={[
								{
									value: 'organization-details',
									icon: Business,
									id: 'organization-details-tab-button',
								},
								{
									value: 'departments',
									icon: LocationCity,
									id: 'departments-tab-button',
								},
							]}
							value={subpage}
						/>
					</GridItem>
				)}
			/>
			<ConditionalRender
				show={!loading}
				onTrue={() => (
					<GridItem xs={12}>
						<Switch>
							<Route
								path={`${props.match.path}/departments`}
								render={(props: any) => <DepartmentsPage account={account} {...props} />}
							/>
							<Route
								path={`${props.match.path}/organization-details`}
								render={(props: any) => (
									<OrganizationDetailsPage account={account} {...props} accounts={accounts} />
								)}
							/>
							<Redirect
								exact
								from={`${props.match.path}`}
								to={`${props.match.path}/organization-details`}
							/>
						</Switch>
					</GridItem>
				)}
			/>
		</GridContainer>
	);
};

export default Details;
